import { formatDistanceToNow, format } from "date-fns";
import { Copy, Lock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ShareLink {
  id: string;
  createdAt: Date;
  expiresAt?: Date;
  password?: string;
  createdBy: {
    id: string;
    name: string;
    username: string;
  };
  url: string;
}

interface RecentLinksProps {
  links: ShareLink[];
}

const RecentLinks = ({ links }: RecentLinksProps) => {
  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Recent Links</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {links.map((link) => (
            <div
              key={link.id}
              className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center space-x-4 flex-grow min-w-0">
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-600">
                      {format(link.createdAt, "MMM d, yyyy HH:MM aaa")}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center space-x-1 cursor-pointer">
                            <Users className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-600">
                              {link.createdBy?.name}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{link.createdBy?.username}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="flex items-center space-x-3 mt-1">
                    {link.expiresAt && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm text-gray-500">
                              Expires{" "}
                              {formatDistanceToNow(link.expiresAt, {
                                addSuffix: true,
                              })}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Expires on {format(link.expiresAt, "PPpp")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {link.password && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center space-x-1">
                              <Lock className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-500">
                                Password protected
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Password: {link.password}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyLink(link.url)}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Copy className="h-4 w-4" />
                <span className="ml-2">Copy</span>
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentLinks;
